import { Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  NgxsOnInit,
  Selector,
  State,
  StateContext,
  Store
} from '@ngxs/store';
import {
  CartCouponAction,
  CartItemsAction,
  CartRewardPointsAction
} from './cart.actions';
import {
  WooCommerceCartFee,
  WooCommerceCartInfo,
  WooCommerceCartItem,
  WooCommerceCartTotals
} from '../../../core/interfaces/woo-commerce/api/cart/cart.interface';
import { WooCommercePostCartItemBody } from '../../../core/interfaces/woo-commerce/cart/cart.interface';
import { WooCommerceCoupon } from '../../../core/interfaces/woo-commerce/api/coupon/coupon-api.interface';
import { WooCommerceCartApiService } from '../../../core/services/woo-commerce/api/woo-commerce-cart-api.service';
import { finalize, from, Observable, of, tap } from 'rxjs';
import { concatMap, last, switchMap } from 'rxjs/operators';
import { WooCommerceCouponApiService } from '../../../core/services/woo-commerce/api/woo-commerce-coupon-api.service';
import { convertHtml2Text } from '../../utils/text.utils';
import { ShippingState } from '../shipping/shipping.state';
import { ShippingMethod } from '../../../core/enums/woo-commerce/shipping/shipping.enum';
import { BaseWooCommerceAddressDetails } from '../../../core/interfaces/woo-commerce/woo-commerce.interface';
import { WooCommerceCartTokenService } from '../../../core/services/woo-commerce/woo-commerce-cart-token.service';
import { HappyStoreStorageService } from '../../../core/services/happy/happy-store-storage.service';

type CartStateLoadingKey =
  | 'cart'
  | 'updateItem'
  | 'deleteItem'
  | 'addItem'
  | 'coupons'
  | 'addCoupon'
  | 'updateCoupon'
  | 'rewardPoints'
  | 'sync';

type CartStateLoading = {
  [key in CartStateLoadingKey]: boolean;
};

export interface PendingAddItem {
  tempKey: string;
  payload: WooCommercePostCartItemBody;
  display: Partial<WooCommerceCartItem>;
}

export interface CartStateModel {
  cart_id: number;
  items: WooCommerceCartItem[];
  fees: WooCommerceCartFee[];
  coupons: WooCommerceCoupon[];
  totals: WooCommerceCartTotals;
  loading: CartStateLoading;
  pendingAdds: PendingAddItem[];
  pendingUpdates: Record<string, number>;
  pendingRemoves: string[];

  has_forced_subscription: boolean;
  has_subscription_option: boolean;
  subscription_id: number | null;
}

const defaultTotals: WooCommerceCartTotals = {
  total_items: '',
  total_items_tax: '',
  total_fees: '',
  total_fees_tax: '',
  total_discount: '',
  total_discount_tax: '',
  total_shipping: '',
  total_shipping_tax: '',
  total_price: '',
  total_tax: '',
  tax_lines: [],
  currency_code: '',
  currency_symbol: '',
  currency_minor_unit: 0,
  currency_decimal_separator: '',
  currency_thousand_separator: '',
  currency_prefix: '',
  currency_suffix: ''
};

const defaults: CartStateModel = {
  cart_id: 0,
  items: [],
  coupons: [],
  fees: [],
  totals: defaultTotals,
  has_forced_subscription: false,
  has_subscription_option: false,
  subscription_id: null,
  loading: {
    cart: false,
    addItem: false,
    deleteItem: false,
    updateItem: false,
    coupons: false,
    addCoupon: false,
    updateCoupon: false,
    rewardPoints: false,
    sync: false
  },
  pendingAdds: [],
  pendingUpdates: {},
  pendingRemoves: []
};

@State<CartStateModel>({
  name: 'cart',
  defaults
})
@Injectable()
export class CartState implements NgxsOnInit {
  static isLoading(key: CartStateLoadingKey) {
    return createSelector([CartState], (state: CartStateModel) => {
      return state?.loading?.[key];
    });
  }

  @Selector()
  static cartId(state: CartStateModel): number {
    return state.cart_id;
  }

  @Selector()
  static items(state: CartStateModel): WooCommerceCartItem[] {
    let items = [...state.items];

    items = items.filter((i) => !state.pendingRemoves.includes(i.key));

    items = items.map((i) => {
      if (state.pendingUpdates[i.key] !== undefined) {
        return { ...i, quantity: state.pendingUpdates[i.key] };
      }
      return i;
    });

    const addedItems = state.pendingAdds.map(
      (a) =>
        ({
          ...a.display,
          key: a.tempKey,
          id: a.payload.id,
          quantity: a.payload.quantity,
          name: a.display.name || 'Item',
          prices: a.display.prices || { price: '0', regular_price: '0' }
        }) as WooCommerceCartItem
    );

    const mergedItems = [...items, ...addedItems];

    return mergedItems.map((item) => ({
      ...item,
      name: convertHtml2Text(item.name)
    }));
  }

  @Selector()
  static hasForcedSubscription(state: CartStateModel): boolean {
    return state.has_forced_subscription;
  }

  @Selector()
  static hasSubscriptionOption(state: CartStateModel): boolean {
    return state.has_subscription_option;
  }

  @Selector()
  static subscriptionId(state: CartStateModel): number | null {
    return state.subscription_id;
  }

  @Selector()
  static hasPendingChanges(state: CartStateModel): boolean {
    return (
      state.pendingAdds.length > 0 ||
      Object.keys(state.pendingUpdates).length > 0 ||
      state.pendingRemoves.length > 0
    );
  }

  @Selector()
  static fees(state: CartStateModel): WooCommerceCartFee[] {
    return state.fees;
  }

  @Selector()
  static driverFee(state: CartStateModel): WooCommerceCartFee | undefined {
    const driverFee = state.fees.find((fee) => {
      return fee.key.startsWith('tip-amount-');
    });

    if (!driverFee) {
      return;
    }

    const rawTipAmount: string = driverFee.key
      .trim()
      .replace(/^tip-amount-/, '')
      .split('-')
      .join('.');

    const tipAmount: number = parseFloat(rawTipAmount);

    return {
      ...driverFee,
      totals: {
        ...driverFee?.totals,
        value: isNaN(tipAmount)
          ? Number(driverFee.totals.total || '0') /
            10 ** (driverFee.totals.currency_minor_unit || 2)
          : tipAmount
      }
    };
  }

  @Selector()
  static totals(state: CartStateModel): WooCommerceCartTotals {
    const backendTotals = state.totals;

    // Calculate total for pending items
    // We can't calculate tax accurately here, so we'll just sum up the line totals
    // The "sync" action will eventually get the correct totals from backend
    let pendingTotal = 0;

    state.pendingAdds.forEach((item) => {
      // Parse price from display data
      const priceStr =
        item.display.prices?.price || item.display.prices?.regular_price || '0';
      const price = parseFloat(priceStr);
      const qty = item.payload.quantity;
      pendingTotal += price * qty;
    });

    // Handle pending updates (quantity changes)
    // We need to adjust the backend total for these items
    let updatesAdjustment = 0;
    Object.entries(state.pendingUpdates).forEach(([key, newQty]) => {
      const item = state.items.find((i) => i.key === key);
      if (item) {
        const currentQty = item.quantity;
        const diff = newQty - currentQty;
        const priceStr = item.prices.price || item.prices.regular_price || '0';
        const itemPrice = parseFloat(priceStr);
        updatesAdjustment += itemPrice * diff;
      }
    });

    // Handle pending removals
    let removalsAdjustment = 0;
    state.pendingRemoves.forEach((key) => {
      const item = state.items.find((i) => i.key === key);
      if (item) {
        const priceStr = item.prices.price || item.prices.regular_price || '0';
        const price = parseFloat(priceStr);
        removalsAdjustment -= price * item.quantity;
      }
    });

    // Calculate final total
    // backendTotals.total_items is likely a string of the total price (e.g. "1050")
    const currentTotal = parseFloat(backendTotals.total_items || '0');
    const totalItems =
      currentTotal + pendingTotal + updatesAdjustment + removalsAdjustment;

    let totalPrice = parseFloat(backendTotals.total_price);
    if (
      state.pendingAdds.length > 0 ||
      state.pendingRemoves.length > 0 ||
      Object.entries(state.pendingUpdates).length > 0
    ) {
      totalPrice += pendingTotal + updatesAdjustment + removalsAdjustment;
    }

    return {
      ...backendTotals,
      total_items: totalItems.toString(),
      total_price: totalPrice.toString() // Approximating total_price as total_items for frontend display
    };
  }

  @Selector()
  static coupons(state: CartStateModel): WooCommerceCoupon[] {
    return state.coupons;
  }

  constructor(
    private wooCommerceCartApiService: WooCommerceCartApiService,
    private wooCommerceCouponApiService: WooCommerceCouponApiService,
    private wooCommerceCartTokenService: WooCommerceCartTokenService,
    private store: Store,
    private happyStoreStorageService: HappyStoreStorageService
  ) {}

  ngxsOnInit(ctx: StateContext<CartStateModel>) {
    try {
      const savedPending =
        this.happyStoreStorageService.getSessionItem('cart_pending');
      const savedState = this.happyStoreStorageService.getSessionItem('cart_state');

      if (savedPending) {
        const { pendingAdds, pendingUpdates, pendingRemoves } =
          JSON.parse(savedPending);
        ctx.patchState({
          pendingAdds: pendingAdds || [],
          pendingUpdates: pendingUpdates || {},
          pendingRemoves: pendingRemoves || []
        });
      }

      if (savedState) {
        const { cart_id, items, fees, coupons, totals } = JSON.parse(savedState);
        ctx.patchState({
          cart_id: cart_id || defaults.cart_id,
          items: items || [],
          fees: fees || [],
          coupons: coupons || [],
          totals: totals || defaultTotals
        });
      }
    } catch (e) {
      console.error('Failed to load cart items', e);
    }
  }

  private savePendingState(state: CartStateModel) {
    try {
      this.happyStoreStorageService.setSessionItem(
        'cart_pending',
        JSON.stringify({
          pendingAdds: state.pendingAdds,
          pendingUpdates: state.pendingUpdates,
          pendingRemoves: state.pendingRemoves
        })
      );
    } catch (e) {
      console.error('Failed to save pending cart items', e);
    }
  }

  private saveCartState(state: CartStateModel) {
    try {
      this.happyStoreStorageService.setSessionItem(
        'cart_state',
        JSON.stringify({
          cart_id: state.cart_id,
          items: state.items,
          fees: state.fees,
          coupons: state.coupons,
          totals: state.totals
        })
      );
    } catch (e) {
      console.error('Failed to save cart state', e);
    }
  }

  @Action(CartItemsAction.Get)
  getCartItems(
    ctx: StateContext<CartStateModel>,
    { payload }: CartItemsAction.Get
  ): Observable<WooCommerceCartInfo> {
    const state = ctx.getState();
    const formatedPayload = { forceUpdate: false, ...payload };

    // If we have items in state (likely loaded from storage), return fake response
    // to skip backend request.
    if (
      (state.items.length > 0 || parseFloat(state.totals.total_price) > 0) &&
      !formatedPayload.forceUpdate
    ) {
      // Return current state as response
      return of({
        items: state.items,
        fees: state.fees,
        coupons: state.coupons,
        totals: state.totals
      } as WooCommerceCartInfo);
    }

    const loading: CartStateLoading = ctx.getState().loading;
    ctx.patchState({ loading: { ...loading, cart: true } });

    const shippingAddress = this.getDeliveryShippingAddress();

    return this.wooCommerceCartApiService.getCart(shippingAddress).pipe(
      tap((response: WooCommerceCartInfo) => {
        const newState = {
          cart_id: response.id || defaults.cart_id,
          items: response.items || defaults.items,
          fees: response.fees || defaults.fees,
          coupons: response.coupons || defaults.coupons,
          totals: response.totals || defaults.totals,
          has_forced_subscription: !!response.has_forced_subscription,
          has_subscription_option: !!response.has_subscription_option,
          subscription_id: response.subscription_id ?? null
        };
        ctx.patchState(newState);
        this.saveCartState({ ...ctx.getState(), ...newState });
      }),
      finalize(() => {
        const updatedLoading: CartStateLoading = ctx.getState().loading;
        ctx.patchState({ loading: { ...updatedLoading, cart: false } });
      })
    );
  }

  @Action(CartItemsAction.Add)
  addCartItem(
    ctx: StateContext<CartStateModel>,
    { payload, displayData }: CartItemsAction.Add
  ): Observable<WooCommerceCartInfo> {
    const state = ctx.getState();
    const tempKey = 'temp-' + Math.random().toString(36).substr(2, 9);

    const pendingAdd: PendingAddItem = {
      tempKey,
      payload,
      display: displayData || {}
    };

    const newState = {
      ...state,
      pendingAdds: [...state.pendingAdds, pendingAdd]
    };

    ctx.patchState(newState);
    this.savePendingState(newState);

    // Fake response for UI compatibility
    const fakeResponse = {
      ...state,
      items: CartState.items(newState),
      totals: state.totals
    } as unknown as WooCommerceCartInfo;

    return of(fakeResponse);
  }

  @Action(CartItemsAction.Update)
  updateCartItem(
    ctx: StateContext<CartStateModel>,
    { payload }: CartItemsAction.Update
  ): Observable<WooCommerceCartInfo> {
    const state = ctx.getState();

    let newState = { ...state };

    if (payload.key.startsWith('temp-')) {
      // Update pending add
      newState.pendingAdds = state.pendingAdds.map((item) =>
        item.tempKey === payload.key
          ? {
              ...item,
              payload: { ...item.payload, quantity: payload.quantity },
              display: { ...item.display, quantity: payload.quantity }
            }
          : item
      );
    } else {
      // Update existing item
      newState.pendingUpdates = {
        ...state.pendingUpdates,
        [payload.key]: payload.quantity
      };
    }

    ctx.patchState(newState);
    this.savePendingState(newState);

    // Fake response
    const fakeResponse = {
      ...state,
      items: CartState.items(newState),
      totals: state.totals
    } as unknown as WooCommerceCartInfo;

    return of(fakeResponse);
  }

  @Action(CartItemsAction.Remove)
  removeCartItem(
    ctx: StateContext<CartStateModel>,
    { payload }: CartItemsAction.Remove
  ): Observable<WooCommerceCartInfo> {
    const state = ctx.getState();
    let newState = { ...state };

    if (payload.startsWith('temp-')) {
      // Remove from pending adds
      newState.pendingAdds = state.pendingAdds.filter(
        (item) => item.tempKey !== payload
      );
    } else {
      // Add to pending removes
      newState.pendingRemoves = [...state.pendingRemoves, payload];
      // Remove from pending updates if present
      const { [payload]: removed, ...restUpdates } = state.pendingUpdates;
      newState.pendingUpdates = restUpdates;
    }

    ctx.patchState(newState);
    this.savePendingState(newState);

    // Fake response
    const fakeResponse = {
      ...state,
      items: CartState.items(newState),
      totals: state.totals
    } as unknown as WooCommerceCartInfo;

    return of(fakeResponse);
  }

  @Action(CartItemsAction.Sync)
  syncCart(ctx: StateContext<CartStateModel>) {
    const state = ctx.getState();
    const shippingAddress = this.getDeliveryShippingAddress();

    // Check if there are any changes
    if (!CartState.hasPendingChanges(state) || state.loading.sync) {
      return of(null);
    }

    const loading: CartStateLoading = state.loading;
    ctx.patchState({ loading: { ...loading, sync: true } });

    const syncOperations: Observable<unknown>[] = [];

    if (state.pendingAdds.length > 0) {
      syncOperations.push(
        this.wooCommerceCartApiService.addCartItemsBatch(
          state.pendingAdds.map((add) => add.payload),
          shippingAddress
        )
      );
    }

    const pendingUpdates = Object.entries(state.pendingUpdates).map(
      ([key, quantity]) => ({ key, quantity })
    );
    if (pendingUpdates.length > 0) {
      syncOperations.push(
        this.wooCommerceCartApiService.updateCartItemsBatch(
          pendingUpdates,
          shippingAddress
        )
      );
    }

    if (state.pendingRemoves.length > 0) {
      syncOperations.push(
        this.wooCommerceCartApiService.removeCartItemsBatch(
          state.pendingRemoves,
          shippingAddress
        )
      );
    }

    const executeOps$ =
      syncOperations.length === 0
        ? of(null)
        : from(syncOperations).pipe(
            concatMap((operation) => operation),
            last()
          );

    return executeOps$.pipe(
      switchMap(() =>
        this.wooCommerceCartApiService.getCart(
          this.getDeliveryShippingAddress()
        )
      ),
      tap((response: WooCommerceCartInfo) => {
        const clearedState = {
          pendingAdds: [],
          pendingUpdates: {},
          pendingRemoves: []
        };
        ctx.patchState(clearedState);
        this.savePendingState({ ...state, ...clearedState });

        const newState = {
          cart_id: response.id || defaults.cart_id,
          items: response.items || defaults.items,
          fees: response.fees || defaults.fees,
          coupons: response.coupons || defaults.coupons,
          totals: response.totals || defaults.totals,
          has_forced_subscription: !!response.has_forced_subscription,
          has_subscription_option: !!response.has_subscription_option
        };
        ctx.patchState(newState);
        this.saveCartState({ ...ctx.getState(), ...newState });
      }),
      finalize(() => {
        const updatedLoading = ctx.getState().loading;
        ctx.patchState({ loading: { ...updatedLoading, sync: false } });
      })
    );
  }

  @Action(CartCouponAction.Get)
  getCartCoupon(
    ctx: StateContext<CartStateModel>
  ): Observable<WooCommerceCoupon[]> {
    const loading: CartStateLoading = ctx.getState().loading;
    ctx.patchState({ loading: { ...loading, coupons: true } });

    return this.wooCommerceCouponApiService.getCoupons().pipe(
      tap((coupons: WooCommerceCoupon[]) => {
        ctx.patchState({ coupons: coupons });
      }),
      finalize(() => {
        const updatedLoading: CartStateLoading = ctx.getState().loading;
        ctx.patchState({ loading: { ...updatedLoading, coupons: false } });
      })
    );
  }

  @Action(CartCouponAction.Add)
  addCartCoupon(
    ctx: StateContext<CartStateModel>,
    { payload }: CartCouponAction.Add
  ): Observable<WooCommerceCartInfo> {
    const loading: CartStateLoading = ctx.getState().loading;
    ctx.patchState({ loading: { ...loading, addCoupon: true } });

    return this.wooCommerceCouponApiService.addCoupon(payload).pipe(
      switchMap(() =>
        this.wooCommerceCartApiService.getCart(
          this.getDeliveryShippingAddress()
        )
      ),
      tap((response: WooCommerceCartInfo) => {
        const newState = {
          cart_id: response.id || defaults.cart_id,
          items: response.items || defaults.items,
          fees: response.fees || defaults.fees,
          coupons: response.coupons || defaults.coupons,
          totals: response.totals || defaults.totals,
          has_forced_subscription: !!response.has_forced_subscription,
          has_subscription_option: !!response.has_subscription_option
        };
        ctx.patchState(newState);
        this.saveCartState({ ...ctx.getState(), ...newState });
      }),
      finalize(() => {
        const updatedLoading: CartStateLoading = ctx.getState().loading;
        ctx.patchState({ loading: { ...updatedLoading, addCoupon: false } });
      })
    );
  }

  @Action(CartCouponAction.Remove)
  removeCartCoupon(
    ctx: StateContext<CartStateModel>,
    { payload }: CartCouponAction.Remove
  ): Observable<WooCommerceCartInfo> {
    const loading: CartStateLoading = ctx.getState().loading;
    ctx.patchState({ loading: { ...loading, updateCoupon: true } });

    return this.wooCommerceCouponApiService.removeCoupon(payload).pipe(
      switchMap(() =>
        this.wooCommerceCartApiService.getCart(
          this.getDeliveryShippingAddress()
        )
      ),
      tap((response: WooCommerceCartInfo) => {
        const newState = {
          cart_id: response.id || defaults.cart_id,
          items: response.items || defaults.items,
          fees: response.fees || defaults.fees,
          coupons: response.coupons || defaults.coupons,
          totals: response.totals || defaults.totals,
          has_forced_subscription: !!response.has_forced_subscription,
          has_subscription_option: !!response.has_subscription_option
        };
        ctx.patchState(newState);
        this.saveCartState({ ...ctx.getState(), ...newState });
      }),
      finalize(() => {
        const updatedLoading: CartStateLoading = ctx.getState().loading;
        ctx.patchState({ loading: { ...updatedLoading, updateCoupon: false } });
      })
    );
  }

  @Action(CartRewardPointsAction.Set)
  setRewardPoints(
    ctx: StateContext<CartStateModel>,
    { payload }: CartRewardPointsAction.Set
  ): Observable<WooCommerceCartInfo> {
    const loading: CartStateLoading = ctx.getState().loading;
    ctx.patchState({ loading: { ...loading, rewardPoints: true } });

    return this.wooCommerceCartApiService
      .setRewardPoints(payload, this.getDeliveryShippingAddress())
      .pipe(
        tap((response: WooCommerceCartInfo) => {
          const newState = {
            cart_id: response.id || defaults.cart_id,
            items: response.items || defaults.items,
            fees: response.fees || defaults.fees,
            coupons: response.coupons || defaults.coupons,
            totals: response.totals || defaults.totals,
            has_forced_subscription: !!response.has_forced_subscription,
            has_subscription_option: !!response.has_subscription_option
          };
          ctx.patchState(newState);
          this.saveCartState({ ...ctx.getState(), ...newState });
        }),
        finalize(() => {
          const updatedLoading: CartStateLoading = ctx.getState().loading;
          ctx.patchState({
            loading: { ...updatedLoading, rewardPoints: false }
          });
        })
      );
  }

  @Action(CartRewardPointsAction.Remove)
  removeRewardPoints(ctx: StateContext<CartStateModel>): Observable<WooCommerceCartInfo> {
    return this.setRewardPoints(ctx, new CartRewardPointsAction.Set(0));
  }

  @Action(CartItemsAction.Clear)
  clearCart(ctx: StateContext<CartStateModel>): void {
    ctx.setState({
      ...defaults,
      loading: ctx.getState().loading
    });
    this.happyStoreStorageService.removeSessionItem('cart_pending');
    this.happyStoreStorageService.removeSessionItem('cart_state');
  }

  private getDeliveryShippingAddress():
    | BaseWooCommerceAddressDetails
    | undefined {
    const shippingState = this.store.selectSnapshot(
      ShippingState.shippingState
    );

    if (shippingState?.method !== ShippingMethod.Delivery) {
      return undefined;
    }

    return shippingState.address;
  }
}
